import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
    TrendingUp, DollarSign, Users, Star, RefreshCw, 
    Award, Clock, Calendar, Plus, Trash2, Search
} from 'lucide-react';
import logger from '@/lib/logger';

const GRADE_COLORS = {
    'A+': 'bg-emerald-500 text-white',
    'A': 'bg-green-500 text-white',
    'B': 'bg-blue-500 text-white',
    'C': 'bg-amber-500 text-white',
    'D': 'bg-red-500 text-white',
};

const formatCur = (a) => new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', maximumFractionDigits: 0 }).format(a || 0);

const ScoreBar = ({ label, score, max, color }) => (
    <div className="flex items-center gap-2 text-xs">
        <span className="w-20 text-muted-foreground">{label}</span>
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${color}`} style={{ width: `${(score / max) * 100}%` }} />
        </div>
        <span className="w-10 text-right font-mono">{score}/{max}</span>
    </div>
);

const EmployeeValueScorePage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [department, setDepartment] = useState('all');
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('scores');
    const [histOpen, setHistOpen] = useState(false);
    const [histComm, setHistComm] = useState({ employee_id: '', month: '', amount: '', description: '' });
    const [histRecords, setHistRecords] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [saving, setSaving] = useState(false);

    const fetchScores = async () => {
        setLoading(true);
        try {
            const params = department && department !== 'all' ? `?department=${department}` : '';
            const res = await api.get(`/hr/employee-value-score${params}`);
            setData(res.data);
        } catch (e) {
            logger.error(e);
            toast.error('Failed to load scores');
        } finally {
            setLoading(false);
        }
    };

    const fetchHistorical = async () => {
        try {
            const res = await api.get('/hr/historical-commissions');
            setHistRecords(res.data);
        } catch (e) { logger.error(e); }
    };

    const fetchEmployees = async () => {
        try {
            const res = await api.get('/hr/reports/generate?report_type=employee_directory');
            setEmployees(res.data.employees || []);
        } catch (e) { logger.error(e); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchScores(); }, [department]);
    useEffect(() => { fetchHistorical(); fetchEmployees(); }, []);

    const addHistorical = async () => {
        if (!histComm.employee_id || !histComm.month || !histComm.amount) {
            toast.error('Fill all required fields');
            return;
        }
        setSaving(true);
        try {
            await api.post('/hr/historical-commissions', {
                employee_id: histComm.employee_id,
                month: histComm.month,
                amount: parseFloat(histComm.amount),
                description: histComm.description
            });
            toast.success('Historical commission added');
            setHistComm({ employee_id: '', month: '', amount: '', description: '' });
            fetchHistorical();
            fetchScores();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to add');
        } finally { setSaving(false); }
    };

    const deleteHistorical = async (id) => {
        try {
            await api.delete(`/hr/historical-commissions/${id}`);
            toast.success('Deleted');
            fetchHistorical();
        } catch (e) { toast.error('Failed to delete'); }
    };

    const filteredScores = (data?.scores || []).filter(s => {
        if (!search) return true;
        const q = search.toLowerCase();
        return s.name?.toLowerCase().includes(q) || s.department?.toLowerCase().includes(q) || s.employee_code?.toLowerCase().includes(q);
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="value-score-loading">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="employee-value-score-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Employee Value Score</h1>
                    <p className="text-sm text-muted-foreground">ROI analysis — Revenue vs Cost, Attendance, Tenure</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 w-[180px]" />
                    </div>
                    <Select value={department} onValueChange={setDepartment}>
                        <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Depts</SelectItem>
                            <SelectItem value="Sales">Sales</SelectItem>
                            <SelectItem value="Customer Service">CS</SelectItem>
                            <SelectItem value="Mentors/Academics">Mentors</SelectItem>
                            <SelectItem value="Operations">Ops</SelectItem>
                            <SelectItem value="Marketing">Marketing</SelectItem>
                            <SelectItem value="Finance">Finance</SelectItem>
                            <SelectItem value="HR">HR</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={fetchScores}><RefreshCw className="h-4 w-4" /></Button>
                </div>
            </div>

            {data && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold">{data.total_employees}</p><p className="text-xs text-muted-foreground">Total</p></CardContent></Card>
                    <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold">{data.avg_score}</p><p className="text-xs text-muted-foreground">Avg Score</p></CardContent></Card>
                    <Card className="border-l-4 border-l-emerald-500"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-emerald-600">{(data.grade_distribution?.['A+'] || 0) + (data.grade_distribution?.['A'] || 0)}</p><p className="text-xs text-muted-foreground">A+ / A</p></CardContent></Card>
                    <Card className="border-l-4 border-l-blue-500"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-blue-600">{data.grade_distribution?.['B'] || 0}</p><p className="text-xs text-muted-foreground">Grade B</p></CardContent></Card>
                    <Card className="border-l-4 border-l-red-500"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-red-600">{(data.grade_distribution?.['C'] || 0) + (data.grade_distribution?.['D'] || 0)}</p><p className="text-xs text-muted-foreground">C / D</p></CardContent></Card>
                </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="scores">Score Board</TabsTrigger>
                    <TabsTrigger value="historical" data-testid="historical-tab">Historical Commissions</TabsTrigger>
                </TabsList>

                <TabsContent value="scores" className="mt-4">
                    <Card>
                        <ScrollArea className="max-h-[600px]">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="sticky left-0 bg-background z-10">#</TableHead>
                                        <TableHead className="sticky left-8 bg-background z-10">Employee</TableHead>
                                        <TableHead>Dept</TableHead>
                                        <TableHead className="text-center">Tenure</TableHead>
                                        <TableHead className="text-right">Revenue</TableHead>
                                        <TableHead className="text-right">Cost</TableHead>
                                        <TableHead className="text-center">ROI</TableHead>
                                        <TableHead className="text-center">Att %</TableHead>
                                        <TableHead className="text-center">Late %</TableHead>
                                        <TableHead className="text-center">Score</TableHead>
                                        <TableHead className="text-center">Grade</TableHead>
                                        <TableHead>Breakdown</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredScores.map((s, i) => (
                                        <TableRow key={s.employee_id} className={s.grade === 'D' ? 'bg-red-50/50 dark:bg-red-950/10' : s.grade === 'A+' ? 'bg-emerald-50/50 dark:bg-emerald-950/10' : ''}>
                                            <TableCell className="sticky left-0 bg-background z-10 font-mono text-xs text-muted-foreground">{i + 1}</TableCell>
                                            <TableCell className="sticky left-8 bg-background z-10">
                                                <div>
                                                    <p className="font-medium text-sm">{s.name}</p>
                                                    <p className="text-[10px] text-muted-foreground">{s.designation}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-xs">{s.department}</TableCell>
                                            <TableCell className="text-center text-xs">{s.tenure_months}m</TableCell>
                                            <TableCell className="text-right text-sm font-medium text-emerald-600">{formatCur(s.total_revenue)}</TableCell>
                                            <TableCell className="text-right text-sm text-red-600">{formatCur(s.total_cost)}</TableCell>
                                            <TableCell className="text-center">
                                                <span className={`text-xs font-bold ${s.roi >= 1 ? 'text-emerald-600' : 'text-red-600'}`}>{s.roi}x</span>
                                            </TableCell>
                                            <TableCell className="text-center text-xs">{s.attendance_pct}%</TableCell>
                                            <TableCell className="text-center text-xs">{s.late_frequency_pct}%</TableCell>
                                            <TableCell className="text-center font-bold text-sm">{s.composite_score}</TableCell>
                                            <TableCell className="text-center">
                                                <Badge className={`${GRADE_COLORS[s.grade]} text-xs`}>{s.grade}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="w-40 space-y-0.5">
                                                    <ScoreBar label="ROI" score={s.score_breakdown.roi_score} max={40} color="bg-emerald-500" />
                                                    <ScoreBar label="Attend." score={s.score_breakdown.attendance_score} max={25} color="bg-blue-500" />
                                                    <ScoreBar label="Punctual" score={s.score_breakdown.punctuality_score} max={15} color="bg-violet-500" />
                                                    <ScoreBar label="Tenure" score={s.score_breakdown.tenure_score} max={10} color="bg-amber-500" />
                                                    <ScoreBar label="Rev/mo" score={s.score_breakdown.revenue_monthly_score} max={10} color="bg-cyan-500" />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    </Card>
                </TabsContent>

                <TabsContent value="historical" className="mt-4">
                    <div className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base flex items-center gap-2"><Plus className="h-5 w-5" /> Add Historical Commission</CardTitle>
                                <CardDescription>Enter pre-ERP commission data to improve ROI accuracy</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-end gap-3 flex-wrap">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Employee</Label>
                                        <Select value={histComm.employee_id} onValueChange={(v) => setHistComm(p => ({ ...p, employee_id: v }))}>
                                            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Select employee" /></SelectTrigger>
                                            <SelectContent>
                                                {employees.map(e => (
                                                    <SelectItem key={e.id} value={e.id}>{e.full_name} ({e.employee_id})</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Month (YYYY-MM)</Label>
                                        <Input value={histComm.month} onChange={(e) => setHistComm(p => ({ ...p, month: e.target.value }))} placeholder="2024-06" className="w-[130px]" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Amount (AED)</Label>
                                        <Input type="number" value={histComm.amount} onChange={(e) => setHistComm(p => ({ ...p, amount: e.target.value }))} placeholder="5000" className="w-[120px]" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Notes</Label>
                                        <Input value={histComm.description} onChange={(e) => setHistComm(p => ({ ...p, description: e.target.value }))} placeholder="Optional" className="w-[180px]" />
                                    </div>
                                    <Button onClick={addHistorical} disabled={saving} data-testid="add-hist-comm-btn">
                                        {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                                        <span className="ml-1">Add</span>
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {histRecords.length > 0 && (
                            <Card>
                                <CardHeader><CardTitle className="text-base">Historical Records ({histRecords.length})</CardTitle></CardHeader>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Employee</TableHead>
                                            <TableHead>Month</TableHead>
                                            <TableHead className="text-right">Amount</TableHead>
                                            <TableHead>Notes</TableHead>
                                            <TableHead className="text-center">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {histRecords.map(r => (
                                            <TableRow key={r.id}>
                                                <TableCell className="font-medium text-sm">{r.employee_name}</TableCell>
                                                <TableCell className="text-sm">{r.month}</TableCell>
                                                <TableCell className="text-right font-medium">{formatCur(r.amount)}</TableCell>
                                                <TableCell className="text-sm text-muted-foreground">{r.description}</TableCell>
                                                <TableCell className="text-center">
                                                    <Button variant="ghost" size="sm" onClick={() => deleteHistorical(r.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Card>
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default EmployeeValueScorePage;
